import ActionType, { ACTION_TYPES } from '@/Common/Enum/ActionType.js';
import cyclopsTagMap from '@/Common/Classifiers/CyclopsTagMapper.js';
import i18n from '@/Plugins/i18n.js';

const t = (key) => i18n.global.t(`common.tag_pills.${key}`);

const getBase = () => ({
  errors: {
    pill: t('record_contains_errors'),
    actionType: ActionType.ACTION_REQUIRED
  },
  warnings: {
    pill: t('record_contains_warnings'),
    actionType: ActionType.ACTION_RECOMMENDED
  },
  'No record': {
    pill: t('record_not_found_pill'),
    actionType: ActionType.ACTION_REQUIRED
  },
  'Too many records': {
    pill: t('multiple_records_pill'),
    actionType: ActionType.ACTION_REQUIRED
  }
});

export const DMARC = () =>
  cyclopsTagMap({
    ...getBase(),
    'p=none': {
      pill: t('policy_on_none'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'p=quarantine': {
      pill: t('policy_on_quarantine'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'p=reject': {
      pill: t('policy_on_reject'),
      actionType: ActionType.NO_ACTION
    },
    'sp=none': {
      pill: t('subdomain_policy_on_none'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'sp=quarantine': {
      pill: t('subdomain_policy_on_quarantine'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'sp=reject': {
      pill: t('subdomain_policy_on_reject'),
      actionType: ActionType.NO_ACTION
    },
    'pct=0': {
      pill: t('policy_not_enforced'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'pct<100': {
      pill: t('policy_not_fully_enforced'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'pct=100': {
      pill: t('policy_fully_enforced'),
      actionType: ActionType.NO_ACTION
    },
    'Too many records': {
      pill: t('multiple_dmarc_records'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'rua=[]': {
      pill: t('no_reporting_addresses'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'ruf=[]': {
      pill: t('no_failure_reporting_addresses'),
      actionType: ActionType.INFO
    },
    'rua=[*]': {
      pill: t('reporting_address_specified'),
      actionType: ActionType.NO_ACTION
    },
    'ruf=[*]': {
      pill: null,
      actionType: ActionType.NO_ACTION
    }
  });

export const SPF = () =>
  cyclopsTagMap({
    ...getBase(),
    'Too many records': {
      pill: t('multiple_spf_records'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'no spf no mx': {
      pill: t('no_spf_no_mx'),
      actionType: ActionType.INFO
    },
    recursive: {
      pill: t('recursive_lookup'),
      actionType: ActionType.ACTION_REQUIRED
    },
    errors: {
      pill: t('record_contains_errors_spf'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'dns>10': {
      pill: t('too_many_dns_lookups'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'dns>6': {
      pill: t('high_dns_lookups'),
      actionType: ActionType.ACTION_RECOMMENDED
    },
    'void>2': {
      pill: t('too_many_void_lookups'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'void>0': {
      pill: t('high_void_lookups'),
      actionType: ActionType.ACTION_RECOMMENDED
    },
    'mxa>10': {
      pill: t('too_many_mx_a_records'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'no all': {
      pill: t('all_missing'),
      actionType: ActionType.ACTION_REQUIRED
    },
    '~all': {
      pill: t('all_soft_fail'),
      actionType: ActionType.NO_ACTION
    },
    '-all': {
      pill: t('all_fail'),
      actionType: ActionType.NO_ACTION
    },
    '?all': {
      pill: t('all_neutral'),
      actionType: ActionType.ACTION_REQUIRED
    },
    '+all': {
      pill: t('all_pass'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'spf reporting': {
      pill: t('obsoleted_spf_reporting'),
      actionType: ActionType.ACTION_RECOMMENDED
    }
  });

export const DKIM = () =>
  cyclopsTagMap({
    ...getBase(),
    'no dkim no mx': {
      pill: t('no_dkim_no_mx'),
      actionType: ActionType.INFO
    },
    'no keys': {
      pill: t('no_known_keys'),
      actionType: ActionType.ACTION_RECOMMENDED
    },
    'invalid keys': {
      pill: t('invalid_keys'),
      actionType: ActionType.ACTION_RECOMMENDED
    },
    'error keys': {
      pill: t('errors_found'),
      actionType: ActionType.ACTION_RECOMMENDED
    },
    'large keys': {
      pill: t('large_keys'),
      actionType: ActionType.INFO
    },
    'weak keys': {
      pill: t('weak_keys'),
      actionType: ActionType.ACTION_RECOMMENDED
    },
    'no errors': {
      pill: t('record_found_pill'),
      actionType: ActionType.NO_ACTION
    },
    'dkim wildcard': {
      pill: t('wildcard_record_exists'),
      actionType: ActionType.ACTION_RECOMMENDED
    },
    'No record': {
      pill: t('no_known_keys_optional'),
      actionType: ActionType.ACTION_OPTIONAL
    }
  });

export const BIMI = () =>
  cyclopsTagMap({
    ...getBase(),
    'Too many records': {
      pill: t('multiple_bimi_records'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'valid logo': {
      pill: t('valid_image'),
      actionType: ActionType.NO_ACTION
    },
    'invalid logo': {
      pill: t('image_errors'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'valid signature': {
      pill: t('valid_certificate'),
      actionType: ActionType.NO_ACTION
    },
    'invalid signature': {
      pill: t('certificate_errors'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'no signature': {
      pill: t('no_certificate'),
      actionType: ActionType.ACTION_REQUIRED
    }
  });

export const TLSRTP = () =>
  cyclopsTagMap({
    ...getBase(),
    'Too many records': {
      pill: t('multiple_tls_rtp_records'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'http rua': {
      pill: t('malformed_rua'),
      actionType: ACTION_TYPES.INFO
    },
    errors: {
      pill: t('record_contains_errors_tls'),
      actionType: ActionType.ACTION_REQUIRED
    }
  });

export const MTASTS = () =>
  cyclopsTagMap({
    ...getBase(),
    'Too many records': {
      pill: t('multiple_mta_sts_records'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'no policy': {
      pill: t('no_policy'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'policy enforce': {
      pill: t('policy_enforce'),
      actionType: ActionType.NO_ACTION
    },
    'policy none': {
      pill: t('policy_none'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'policy testing': {
      pill: t('policy_testing'),
      actionType: ActionType.ACTION_REQUIRED
    },
    'maxage<day': {
      pill: t('expiry_less_than_day'),
      actionType: ActionType.INFO
    },
    errors: {
      pill: t('record_contains_errors_mta'),
      actionType: ActionType.ACTION_REQUIRED
    }
  });
